import { Col, Divider, Row } from 'antd';
import { isEqual, isNil } from 'lodash';
import pluralize from 'pluralize';

import {
  getAbilityToPayLabel,
  getClientSizeLabel,
  getYearsInBusinessLabel,
} from 'utils/clientUtils';
import {
  DurationTypeEnum,
  CustomerHistoryDetailsModel,
  FullCustomerInfoResponseModel,
} from 'api/adminApiClient';
import { getYesNoAnswer } from 'components/ActivitiesDetailsSection/HistorySection/utils';
import HistoryLog from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryLog';
import HistoryField from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryField';
import { formatNumberAsCurrency } from 'utils/numberUtils';

import styles from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryCard.module.less';

type Props = {
  historyDetails: CustomerHistoryDetailsModel;
  historyId: string;
};

export default function AdditionalInformationDetailsCard(props: Props) {
  const { historyDetails, historyId } = props;
  const { oldData, newData } = historyDetails;

  const hasPotentialHeadcountChanged = !isEqual(
    oldData?.generalInfo?.additionalInfo?.potentialHeadcount,
    newData?.generalInfo?.additionalInfo?.potentialHeadcount,
  );
  const hasIndustryChanged = !isEqual(
    oldData?.generalInfo?.additionalInfo?.industryName,
    newData?.generalInfo?.additionalInfo?.industryName,
  );
  const hasYearsInBusinessChanged = !isEqual(
    oldData?.generalInfo?.additionalInfo?.yearsInBusiness,
    newData?.generalInfo?.additionalInfo?.yearsInBusiness,
  );
  const hasEstimatedProjectLengthChanged =
    !isEqual(
      oldData?.generalInfo?.additionalInfo?.estimatedType,
      newData?.generalInfo?.additionalInfo?.estimatedType,
    ) ||
    !isEqual(
      oldData?.generalInfo?.additionalInfo?.estimatedValue,
      newData?.generalInfo?.additionalInfo?.estimatedValue,
    );
  const hasAbilityToPayChanged = !isEqual(
    oldData?.generalInfo?.additionalInfo?.abilityToPay,
    newData?.generalInfo?.additionalInfo?.abilityToPay,
  );
  const hasAverageBillRateChanged = !isEqual(
    oldData?.generalInfo?.additionalInfo?.averageBillRate,
    newData?.generalInfo?.additionalInfo?.averageBillRate,
  );
  const hasBusinessBeforeChanged = !isEqual(
    oldData?.generalInfo?.additionalInfo?.haveBusinessBefore,
    newData?.generalInfo?.additionalInfo?.haveBusinessBefore,
  );

  const getProjectEstimation = (
    estimatedValue?: number,
    estimatedType?: DurationTypeEnum,
  ) => {
    if (isNil(estimatedValue) && isNil(estimatedType)) return;

    let type = '';

    switch (estimatedType) {
      case DurationTypeEnum.Week:
        type = 'week';
        break;

      case DurationTypeEnum.Month:
        type = 'month';
        break;

      case DurationTypeEnum.Year:
        type = 'year';
        break;
    }

    if (!isNil(estimatedValue)) {
      return pluralize(type, estimatedValue, true);
    } else {
      // https://mybasepay.atlassian.net/browse/MBO-2727
      return ` N/A ${type}s`;
    }
  };

  const renderAddiotionalInfo = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    const sectionName = 'additional-information';

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Additional information</div>
          </Col>

          {hasPotentialHeadcountChanged && (
            <HistoryField
              title="Potential headcount"
              value={getClientSizeLabel(
                customerDetails?.generalInfo?.additionalInfo
                  ?.potentialHeadcount,
              )}
              dataId={`${sectionName}-potential-headcount-field`}
            />
          )}

          {hasIndustryChanged && (
            <HistoryField
              title="Industry"
              value={customerDetails?.generalInfo?.additionalInfo?.industryName}
              dataId={`${sectionName}-industry-field`}
            />
          )}

          {hasYearsInBusinessChanged && (
            <HistoryField
              title="Years in business"
              value={
                !isNil(
                  customerDetails?.generalInfo?.additionalInfo?.yearsInBusiness,
                )
                  ? getYearsInBusinessLabel(
                      customerDetails?.generalInfo?.additionalInfo
                        ?.yearsInBusiness,
                    )
                  : undefined
              }
              dataId={`${sectionName}-years-business-field`}
            />
          )}

          {hasEstimatedProjectLengthChanged && (
            <HistoryField
              title="Estimated length of the project"
              value={getProjectEstimation(
                customerDetails?.generalInfo?.additionalInfo?.estimatedValue,
                customerDetails?.generalInfo?.additionalInfo?.estimatedType,
              )}
              dataId={`${sectionName}-estimated-value-field`}
            />
          )}

          {hasAbilityToPayChanged && (
            <HistoryField
              title="Ability to pay"
              value={
                !isNil(
                  customerDetails?.generalInfo?.additionalInfo?.abilityToPay,
                )
                  ? getAbilityToPayLabel(
                      customerDetails?.generalInfo?.additionalInfo
                        ?.abilityToPay,
                    )
                  : undefined
              }
              dataId={`${sectionName}-ability-pay-field`}
            />
          )}

          {hasAverageBillRateChanged && (
            <HistoryField
              title="Average bill rate"
              value={formatNumberAsCurrency({
                num: customerDetails?.generalInfo?.additionalInfo
                  ?.averageBillRate,
              })}
              dataId={`${sectionName}-ability-bill-rate-field`}
            />
          )}

          {hasBusinessBeforeChanged && (
            <HistoryField
              title="Have you done business with them before?"
              value={getYesNoAnswer(
                customerDetails?.generalInfo?.additionalInfo
                  ?.haveBusinessBefore,
              )}
              dataId={`${sectionName}-have-business-before-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  return (
    <HistoryLog
      historyId={historyId}
      oldData={renderAddiotionalInfo(oldData)}
      newData={renderAddiotionalInfo(newData)}
    />
  );
}
